import React from 'react';
import { motion } from 'framer-motion';
import { UserIcon, ShieldCheckIcon, KeyIcon, LockIcon, CheckIcon, HomeIcon, UsersIcon, PawPrintIcon, Building2Icon, CarIcon, CloudIcon, DatabaseIcon, CodeIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.6,
    ease: EASE
  }
};
type Bilingual = {
  en: string;
  fr: string;
};

/* ---------- Card visuals ---------- */

function DigitalIdentityVisual() {
  return <div className="relative w-[11.5rem] h-[6.5rem]" data-id="element-3969" data-mp="32wjck865ujxc">
      <div className="absolute inset-0 rounded-xl bg-[#4F46E5]/[0.07] flex items-center gap-3 px-4" data-id="element-3970" data-mp="2xz9oowyf6gbm">
        <span className="w-11 h-11 rounded-lg bg-[#4F46E5]/20 flex items-center justify-center" data-id="element-3971" data-mp="i7c61q5e92sx">
          <UserIcon className="w-6 h-6 text-[#4F46E5]" strokeWidth={2} aria-hidden="true" data-id="element-3972" data-mp="d88z205usmib" />
        </span>
        <span className="flex-1 space-y-2" data-id="element-3973" data-mp="3r4v4zxgpp8qh">
          <span className="block h-2 rounded-full bg-[#4F46E5]/25" data-id="element-3974" data-mp="3w0k9mf7l9ftn" />
          <span className="block h-2 rounded-full bg-[#4F46E5]/15 w-3/4" data-id="element-3975" data-mp="4vk6415fkqkz" />
        </span>
      </div>
      <span className="absolute -right-1 bottom-1 w-8 h-8 rounded-full bg-[#4F46E5] flex items-center justify-center shadow-[0_2px_8px_rgba(79,70,229,0.35)]" data-id="element-3976" data-mp="9u3u073l4cfz">
        <CheckIcon className="w-4 h-4 text-white" strokeWidth={3} aria-hidden="true" data-id="element-3977" data-mp="38duapmqje1d9" />
      </span>
    </div>;
}
function OpenSsoVisual() {
  const tile = 'w-[3.1rem] h-[3.1rem] rounded-xl bg-white border border-[#E4E6EC] shadow-[0_1px_4px_rgba(18,18,18,0.06)] flex items-center justify-center';
  return <div className="grid grid-cols-2 gap-2.5" data-id="element-3978" data-mp="2pm1g5nvtk83f">
      <span className={tile} data-id="element-3979" data-mp="2pl9kkd282ad5">
        <span className="w-6 h-6 rounded-full" style={{
        background: 'conic-gradient(#EA4335 0deg 90deg, #FBBC05 90deg 180deg, #34A853 180deg 270deg, #4285F4 270deg 360deg)',
        WebkitMask: 'radial-gradient(circle, transparent 54%, #000 56%)',
        mask: 'radial-gradient(circle, transparent 54%, #000 56%)'
      }} data-id="element-3980" data-mp="2ukmgdnb2mrrv" />
      </span>
      <span className={tile} data-id="element-3981" data-mp="2rawvgpncgu1n">
        <span className="grid grid-cols-2 gap-[3px]" data-id="element-3982" data-mp="2rboez23o31bk">
          <span className="w-2.5 h-2.5 bg-[#F25022]" data-id="element-3983" data-mp="2rczm9bwbmr7f" />
          <span className="w-2.5 h-2.5 bg-[#7FBA00]" data-id="element-3984" data-mp="23lu5muik3uw2" />
          <span className="w-2.5 h-2.5 bg-[#00A4EF]" data-id="element-3985" data-mp="1ym7qugn2y6l5" />
          <span className="w-2.5 h-2.5 bg-[#FFB900]" data-id="element-3986" data-mp="1yniv0hkftgcs" />
        </span>
      </span>
      <span className={tile} data-id="element-3987" data-mp="2dk9zh58yxi99">
        <span className="w-5 h-5 rounded-full bg-[#121212]/85" data-id="element-3988" data-mp="2dh5kdctyt23t" />
      </span>
      <span className="w-[3.1rem] h-[3.1rem] rounded-xl bg-[#4F46E5]/[0.09] flex items-center justify-center" data-id="element-3989" data-mp="28jccmfd0k01f">
        <KeyIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={2} aria-hidden="true" data-id="element-3990" data-mp="3owkgkeecnpxv" />
      </span>
    </div>;
}
function TwoFactorVisual() {
  return <div className="relative w-[5.5rem] h-[7rem]" data-id="element-3991" data-mp="3rh8racn95d0l">
      <div className="absolute inset-0 rounded-2xl border-2 border-[#121212]/80 bg-white flex flex-col items-center justify-center gap-3" data-id="element-3992" data-mp="d1vp2vshjbn">
        <span className="w-9 h-9 rounded-lg bg-[#4F46E5]/[0.09] flex items-center justify-center" data-id="element-3993" data-mp="3ijq00qj0m2g9">
          <LockIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={2} aria-hidden="true" data-id="element-3994" data-mp="3mk8tsq4gxil3" />
        </span>
        <span className="flex gap-1" data-id="element-3995" data-mp="j2qmcjslydl7">
          {[0, 1, 2, 3, 4].map(dot => <span key={dot} className="w-1.5 h-1.5 rounded-full bg-[#4F46E5]/40" data-id="element-3996" data-mp="j4usl8dzboon" />)}
        </span>
      </div>
      <span className="absolute -right-2 bottom-3 w-8 h-8 rounded-full bg-[#4F46E5] flex items-center justify-center shadow-[0_2px_8px_rgba(79,70,229,0.35)]" data-id="element-3997" data-mp="9bnmtmfbu6o1">
        <CheckIcon className="w-4 h-4 text-white" strokeWidth={3} aria-hidden="true" data-id="element-3998" data-mp="11u1baverwgag" />
      </span>
    </div>;
}
function RelationshipsVisual() {
  const node = 'w-9 h-9 rounded-full bg-[#4F46E5]/[0.09] flex items-center justify-center';
  return <div className="relative w-[11.5rem] h-[6.5rem]" data-id="element-3999" data-mp="30c4p02bmqmvq">
      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[7.5rem] h-[3.75rem] rounded-full border border-[#4F46E5]/20" aria-hidden="true" data-id="element-4000" data-mp="30co9nqsnzpdg" />
      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-[#4F46E5]/15 flex items-center justify-center" data-id="element-4001" data-mp="3t0k38mmxt160">
        <UserIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={2} aria-hidden="true" data-id="element-4002" data-mp="3jaqsfnumcq2t" />
      </span>
      <span className={`${node} absolute left-0 top-0`} data-id="element-4003" data-mp="3f95kmgwxz1zh">
        <HomeIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={2} aria-hidden="true" data-id="element-4004" data-mp="fmp4fp307uej" />
      </span>
      <span className={`${node} absolute right-0 top-0`} data-id="element-4005" data-mp="atw032bg6o2z">
        <UsersIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={2} aria-hidden="true" data-id="element-4006" data-mp="1tiqgpxdvhjxx" />
      </span>
      <span className={`${node} absolute left-0 bottom-0`} data-id="element-4007" data-mp="1yhtlrhturq5w">
        <PawPrintIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={2} aria-hidden="true" data-id="element-4008" data-mp="22idi4j6lqpqz" />
      </span>
      <span className={`${node} absolute left-1/2 -translate-x-1/2 bottom-0`} data-id="element-4009" data-mp="27j9m0wqyebwe">
        <CarIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={2} aria-hidden="true" data-id="element-4010" data-mp="2cg9s37nszeni" />
      </span>
      <span className={`${node} absolute right-0 bottom-0`} data-id="element-4011" data-mp="2hdt5y6gl9pex">
        <Building2Icon className="w-4 h-4 text-[#4F46E5]" strokeWidth={2} aria-hidden="true" data-id="element-4012" data-mp="2m85pshlz92r7" />
      </span>
    </div>;
}
function ConsentVisual() {
  return <div className="relative w-[8rem] h-[6.75rem]" data-id="element-4013" data-mp="2v8vihcfxyd0p">
      <div className="absolute inset-y-0 left-2 right-2 rounded-xl bg-white border border-[#E4E6EC] shadow-[0_1px_4px_rgba(18,18,18,0.05)] p-4 space-y-2.5" data-id="element-4014" data-mp="306ndwgfsoh9n">
        <span className="block h-2 rounded-full bg-[#4F46E5]/25 w-2/3" data-id="element-4015" data-mp="104xgdtzqqaxc" />
        <span className="block h-2 rounded-full bg-[#4F46E5]/15" data-id="element-4016" data-mp="v6ci4041u1bj" />
        <span className="block h-2 rounded-full bg-[#4F46E5]/15 w-5/6" data-id="element-4017" data-mp="19u7dxpdcslsu" />
        <span className="block h-2 rounded-full bg-[#4F46E5]/15 w-1/2" data-id="element-4018" data-mp="19waa6r53nb60" />
      </div>
      <span className="absolute right-0 bottom-2 w-9 h-9 rounded-full bg-[#4F46E5] flex items-center justify-center shadow-[0_2px_8px_rgba(79,70,229,0.35)]" data-id="element-4019" data-mp="151w8k6zug7sw">
        <ShieldCheckIcon className="w-4 h-4 text-white" strokeWidth={2.25} aria-hidden="true" data-id="element-4020" data-mp="1ettrjzx3g426" />
      </span>
    </div>;
}
function PermissionsVisual({
  roles
}: {
  roles: string[];
}) {
  return <div className="flex items-center gap-3" data-id="element-4021" data-mp="1nui4erar3ydh">
      <span className="w-11 h-11 shrink-0 rounded-full bg-[#4F46E5]/[0.09] flex items-center justify-center" data-id="element-4022" data-mp="3uk6gtkfjk4ol">
        <UserIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={2} aria-hidden="true" data-id="element-4023" data-mp="3plv677hf9q5b" />
      </span>
      <span className="space-y-1.5" data-id="element-4024" data-mp="253t0b7vyp7fc">
        {roles.map(role => <span key={role} className="flex items-center justify-between gap-3 rounded-md border border-[#E4E6EC] bg-white pl-2.5 pr-1.5 py-1" data-id="element-4025" data-mp="2a2nj49txivzg">
            <span className="font-sans text-[11px] text-[#121212]/75" data-id="element-4026" data-mp="20cb1apdi1gx0">
              {role}
            </span>
            <span className="w-7 h-4 rounded-full bg-[#4F46E5] flex items-center justify-end px-0.5" data-id="element-4027" data-mp="1mc9wwzy0zrcs">
              <span className="w-3 h-3 rounded-full bg-white" data-id="element-4028" data-mp="1rc641820rywk" />
            </span>
          </span>)}
      </span>
    </div>;
}
function OpenApisVisual() {
  return <div className="relative w-[10.5rem] h-[6.5rem]" data-id="element-4029" data-mp="1hdq9ev3sa7ib">
      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 px-3 py-2 rounded-lg bg-[#4F46E5] font-sans text-xs font-semibold text-white" data-id="element-4030" data-mp="1he8g6lo2o44h">
        API
      </span>
      <span className="absolute left-0 top-2 w-11 h-11 rounded-full bg-[#4F46E5]/[0.09] flex items-center justify-center" data-id="element-4031" data-mp="3bqg7lp16q2vi">
        <CloudIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={2} aria-hidden="true" data-id="element-4032" data-mp="1zhkzlhjfjar7" />
      </span>
      <span className="absolute right-2 top-0 w-11 h-11 rounded-full bg-[#4F46E5]/[0.09] flex items-center justify-center" data-id="element-4033" data-mp="1psb6pzgrho21">
        <DatabaseIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={2} aria-hidden="true" data-id="element-4034" data-mp="1ksgki97xlmnb" />
      </span>
      <span className="absolute right-3 bottom-0 w-11 h-11 rounded-full bg-[#4F46E5]/[0.09] flex items-center justify-center" data-id="element-4035" data-mp="1fs28kg37lsk0">
        <CodeIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={2} aria-hidden="true" data-id="element-4036" data-mp="16tgxf602hft9" />
      </span>
      <span className="absolute left-[2.6rem] top-1/2 w-8 h-px bg-[#4F46E5]/25" aria-hidden="true" data-id="element-4037" data-mp="11w6vwrzeldp2" />
      <span className="absolute right-[2.9rem] top-[1.4rem] w-7 h-px bg-[#4F46E5]/25 rotate-[-25deg]" aria-hidden="true" data-id="element-4038" data-mp="315ng0pvaiyvl" />
      <span className="absolute right-[3.1rem] bottom-[1.6rem] w-7 h-px bg-[#4F46E5]/25 rotate-[25deg]" aria-hidden="true" data-id="element-4039" data-mp="33sxwqjbfqtef" />
    </div>;
}
function TrustedDataVisual() {
  return <div className="relative w-[7rem] h-[6.5rem]" data-id="element-4040" data-mp="3dhpkef9gqrjj">
      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1" data-id="element-4041" data-mp="3dk28la5tdy51">
        {[0, 1, 2].map(layer => <span key={layer} className="w-20 h-6 rounded-full bg-[#4F46E5]/25 border border-[#4F46E5]/25" data-id="element-4042" data-mp="2kud8cz0xi24h" />)}
      </span>
      <span className="absolute right-0 bottom-3 w-9 h-9 rounded-full bg-[#4F46E5] flex items-center justify-center shadow-[0_2px_8px_rgba(79,70,229,0.35)]" data-id="element-4043" data-mp="2urrcbz5o0yfw">
        <CheckIcon className="w-4 h-4 text-white" strokeWidth={3} aria-hidden="true" data-id="element-4044" data-mp="2zrvvjwxyk79t" />
      </span>
    </div>;
}

/* ---------- Content ---------- */

const ROLES: {
  en: string[];
  fr: string[];
} = {
  en: ['Resident', 'Employee', 'Partner', 'Supplier'],
  fr: ['Citoyen', 'Employé', 'Partenaire', 'Fournisseur']
};
const CARDS: {
  id: string;
  title: Bilingual;
  body: Bilingual;
  visual: 'identity' | 'sso' | 'twofactor' | 'relationships' | 'consent' | 'permissions' | 'apis' | 'data';
}[] = [{
  id: 'digital-identity',
  title: {
    en: 'Digital identity',
    fr: 'Identité numérique'
  },
  body: {
    en: 'A unique, verified and persistent identity for all your services.',
    fr: 'Une identité unique, vérifiée et persistante pour tous vos services.'
  },
  visual: 'identity'
}, {
  id: 'open-sso',
  title: {
    en: 'Open SSO',
    fr: 'SSO ouvert'
  },
  body: {
    en: 'A single sign-on for all services. Compatible with your identity providers (Google, Microsoft, Apple, etc.).',
    fr: 'Une seule connexion pour tous les services. Compatible avec vos fournisseurs d’identité (Google, Microsoft, Apple, etc.).'
  },
  visual: 'sso'
}, {
  id: 'two-factor',
  title: {
    en: 'Multi-factor authentication (MFA)',
    fr: 'Authentification multifacteur (AMF)'
  },
  body: {
    en: 'Enterprise-grade security for every account.',
    fr: 'Une sécurité de calibre professionnel pour chaque compte.'
  },
  visual: 'twofactor'
}, {
  id: 'relationships',
  title: {
    en: 'Profiles & relationships',
    fr: 'Profils et relations'
  },
  body: {
    en: 'Person, family, property, pet, vehicle, organization. A complete view of the relationships that connect you.',
    fr: 'Personne, famille, propriété, animal, véhicule, organisation. Une vue complète des relations qui vous connectent.'
  },
  visual: 'relationships'
}, {
  id: 'consent',
  title: {
    en: 'Consent & privacy',
    fr: 'Consentements et protection des renseignements personnels'
  },
  body: {
    en: 'Granular consent management, traceability and integrated compliance (e.g., Law 25).',
    fr: 'Gestion granulaire des consentements, traçabilité et conformité intégrée (p. ex. la Loi 25).'
  },
  visual: 'consent'
}, {
  id: 'permissions',
  title: {
    en: 'Rights & permissions',
    fr: 'Droits et permissions'
  },
  body: {
    en: 'Roles, authorizations and access controls based on context, service and organization.',
    fr: 'Rôles, autorisations et accès contrôlés selon le contexte, le service et l’organisation.'
  },
  visual: 'permissions'
}, {
  id: 'open-apis',
  title: {
    en: 'Open APIs',
    fr: 'API ouvertes'
  },
  body: {
    en: 'An identity you can share and integrate with your existing systems and partners.',
    fr: 'Une identité partageable et intégrable avec vos systèmes existants et partenaires.'
  },
  visual: 'apis'
}, {
  id: 'trusted-data',
  title: {
    en: 'Trusted data',
    fr: 'Données de confiance'
  },
  body: {
    en: 'Reliable, up-to-date data that can be reused across all services for better decisions.',
    fr: 'Des données fiables, à jour et réutilisables dans tous les services, pour de meilleures décisions.'
  },
  visual: 'data'
}];
export function TrustFoundation() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (value: Bilingual) => isFR ? value.fr : value.en;
  const roles = isFR ? ROLES.fr : ROLES.en;
  const renderVisual = (visual: (typeof CARDS)[number]['visual']) => {
    switch (visual) {
      case 'identity':
        return <DigitalIdentityVisual data-id="element-4045" data-mp="2wnv258xen3ul" />;
      case 'sso':
        return <OpenSsoVisual data-id="element-4046" data-mp="2wmbg11p2hykw" />;
      case 'twofactor':
        return <TwoFactorVisual data-id="element-4047" data-mp="2rqw5ojq9qkt8" />;
      case 'relationships':
        return <RelationshipsVisual data-id="element-4048" data-mp="36e7jks9xagwi" />;
      case 'consent':
        return <ConsentVisual data-id="element-4049" data-mp="36fsdcfoexvtb" />;
      case 'permissions':
        return <PermissionsVisual roles={roles} data-id="element-4050" data-mp="31nhab3ydltwj" />;
      case 'apis':
        return <OpenApisVisual data-id="element-4051" data-mp="2dy6jae6tvdvl" />;
      case 'data':
        return <TrustedDataVisual data-id="element-4052" data-mp="2dwvdy9xqhy2d" />;
    }
  };
  return <div className="max-w-[1320px] mx-auto" data-id="element-4053" data-mp="28z3d2qf431m1">
      <motion.div {...reveal} className="max-w-3xl mx-auto text-center" data-id="element-4054" data-mp="28yagujds3ui5">
        <p className="font-sans text-xs font-semibold tracking-[0.28em] text-[#4F46E5] uppercase" data-id="element-4055" data-mp="1c888mxzan1it">
          {isFR ? 'Identité' : 'Identity'}
        </p>
        <h2 className="font-serif text-4xl md:text-5xl text-[#121212] leading-[1.14] tracking-tight mt-5" data-id="element-4056" data-mp="1g77ecksun2bb">
          <span className="block" data-id="element-4057" data-mp="1l6jyuyw8zxaq">
            {isFR ? 'Bien plus qu’une connexion.' : 'More than a login.'}
          </span>
          <span className="block" data-id="element-4058" data-mp="1uypow32xw4na">
            {isFR ? 'Une fondation d’identité de confiance.' : 'A trusted identity foundation.'}
          </span>
        </h2>
        <p className="font-sans text-lg text-[#121212]/60 leading-relaxed mt-5" data-id="element-4059" data-mp="23z4c57zgrttf">
          {isFR ? 'Une seule identité relie les personnes, leurs relations, leurs droits, leurs consentements et les services de tout l’écosystème.' : 'One identity connects people, relationships, rights, consent and services across the entire ecosystem.'}
        </p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-14" data-id="element-4060" data-mp="70f7n39vc9xh">
        {CARDS.map((card, index) => <motion.article key={card.id} {...reveal} transition={{
        duration: 0.6,
        delay: index % 4 * 0.06,
        ease: EASE
      }} className="flex flex-col rounded-2xl border border-[#E4E6EC] bg-white px-6 py-7 shadow-[0_1px_3px_rgba(18,18,18,0.04)]" data-id="element-4061" data-mp="296zsbo6swmf" data-mpi={index}>
            <div className="h-[8.5rem] flex items-center justify-center" data-id="element-4062" data-mp="px6utnsj796t">
              {renderVisual(card.visual)}
            </div>
            <h3 className="font-serif text-xl text-[#121212] leading-snug mt-5" data-id="element-4063" data-mp="g0kd37vi4y90">
              {pick(card.title)}
            </h3>
            <p className="font-sans text-sm text-[#5B6478] leading-relaxed mt-2.5" data-id="element-4064" data-mp="byqh3cw94if6">
              {pick(card.body)}
            </p>
          </motion.article>)}
      </div>

      <motion.div {...reveal} transition={{
      duration: 0.6,
      delay: 0.1,
      ease: EASE
    }} className="mt-12 pt-8 border-t border-[#E4E6EC]" data-id="element-4065" data-mp="2wphfkis1dz17">
        <p className="font-sans text-base text-[#121212]/55 text-center" data-id="element-4066" data-mp="335zficz3qsjt">
          {isFR ? 'Une personne. Une identité de confiance. Toutes ses relations et ses services connectés.' : 'One person. One trusted identity. Every relationship and service connected.'}
        </p>
      </motion.div>
    </div>;
}